import { Intent } from "@blueprintjs/core";
import axios from "axios";
import { LinkButton } from "components/buttons";
import { Filter, FilterButton } from "components/filtering";
import {
    FormCheckbox,
    FormLocationSelector,
    FormMultiSelect,
    FormReadonly,
    FormTextInput,
} from "components/form-fields";
import { FriendlyBoolean } from "components/formatting/FriendlyBoolean";
import { GridLayout } from "components/grid";
import { ListingTable } from "components/listing";
import { Breadcrumbs } from "components/navigation";
import {
    AlertUnsavedData,
    NotificationInline,
    NotificationToaster,
} from "components/notifications";
import { EmployeeStatus } from "components/status";
import { Tooltip } from "components/tooltip";
import { Wizard } from "components/wizard";
import { useDebounce } from "hooks/useDebounce";
import { useValidation } from "hooks/useValidation";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmployeeService, GreyFleetService, LicenceCheckingService } from "services";

export function GreyFleetCreate({
    breadcrumbs = []
}) {
    const badRequestCode = 400;
    var navigate = useNavigate();
    const debounceTimeout = 750;
    const pageSize = 25;
    const headers = [
        "Employee Name",
        "Location",
        "Email",
        "Existing Licence Check (Last 28 days)",
        "Select",
    ];
    const sortableHeaders = [
        { headerName: "Employee Name", sortName: "NAME" },
        { headerName: "Email", sortName: "EMAIL" },
        { headerName: "Location", sortName: "LOCATION" },
    ];
    // General
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    // Wizard
    const [canProceed, setCanProceed] = useState(true);
    const [wizardStep, setWizardStep] = useState(0);
    // Search
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [pageNumber, setPageNumber] = useState(1);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");
    // Refs to avoid useEffect loops
    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentSearchTerm = useRef();
    // Options
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [coverTypes, setCoverTypes] = useState([]);
    // Grey Fleet Settings
    const [greyFleetSettings, setGreyFleetSettings] = useState({ vehiclesPerEmployee: 0 });
    // Search results
    const [employeesFromSearch, setEmployeesFromSearch] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    // Wizard step 2
    const [isValidPage2, errorsPage2, validatePage2] = useValidation(false);
    const [originalEmail, setOriginalEmail] = useState("");
    const [emailAndRegistration, setEmailAndRegistration] = useState({
        emailAddress: "",
        registration: "",
        ignorePolicyCheck: false,
        requiredCoverTypeIds: [],
    });

    useEffect(() => {
        axios
            .all([
                EmployeeService.getEmployeeOptions(),
                LicenceCheckingService.getGreyFleetSettings(),
                GreyFleetService.getInsuranceCoverTypes(),
            ])
            .then(
                function ([employeeOptions, greyFleetSettingsData, coverTypesData]) {
                    setGreyFleetSettings(greyFleetSettingsData);
                    setEmployeeStatusOptions(employeeOptions.employeeStatuses);
                    setEmployeeTypeOptions(employeeOptions.employeeTypes);
                    setCoverTypes(coverTypesData ?? []);

                    setPageFilters([
                        {
                            name: "Status",
                            displayName: "Status",
                            items: employeeOptions.employeeStatuses.filter(
                                (status) => {
                                    return status.active;
                                }
                            ),
                        },
                        {
                            name: "Type",
                            displayName: "Type",
                            items: employeeOptions.employeeTypes,
                        },
                        {
                            name: "Category",
                            displayName: "Category",
                            items: employeeOptions.employeeCategories,
                        },
                    ]);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load settings. Please refresh the page."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not load settings. Please refresh the page."
                );
            })
            .then(() => {
                setIsLoadingOptions(false);
            });
    }, []);

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        isLoadingOptions,
    ]);

    useEffect(() => {
        if (isLoading || isLoadingOptions || isSaving) {
            setCanProceed(false);
        } else if (wizardStep === 0 && selectedEmployee != null) {
            setCanProceed(true);
        } else if (wizardStep === 1 && isValidPage2) {
            setCanProceed(true);
        } else if (wizardStep === 2) {
            setCanProceed(true);
        } else {
            setCanProceed(false);
        }
    }, [
        isLoading,
        isLoadingOptions,
        isSaving,
        wizardStep,
        selectedEmployee,
        isValidPage2,
        emailAndRegistration,
    ]);

    useEffect(() => {
        if (employeesFromSearch == null || employeesFromSearch.length === 0) {
            setTableRows([]);
            setTotalRecords(0);
            return;
        }
        const rows = employeesFromSearch.data.map((e) => {
            const hasMaximumChecks = greyFleetSettings?.vehiclesPerEmployee != null
                && greyFleetSettings.vehiclesPerEmployee > 0
                && e.existingGreyFleetChecks >= greyFleetSettings.vehiclesPerEmployee;
            const checkbox = <FormCheckbox
                                key={`selectEmployee-${e.id}`}
                                checked={!hasMaximumChecks && selectedEmployee?.id === e.id}
                                onChange={() => {
                                    setSelectedEmployee(e);
                                    setEmailAndRegistration((prev) => ({
                                        ...prev,
                                        emailAddress: e.email ?? "",
                                        ignorePolicyCheck: false,
                                    }));
                                    setOriginalEmail(e.email ?? "");
                                }}
                                disabled={hasMaximumChecks}
                            />;
            return [
                <div key={e.id} className="inline-items">
                    <EmployeeStatus
                        statusId={e.statusId}
                        typeId={e.typeId}
                        typeAttributeMappings={employeeTypeOptions}
                        statusAttributeMappings={employeeStatusOptions}
                    />
                    {e.name}
                </div>,
                e.location,
                e.email ? e.email : "-",
                <FriendlyBoolean key={`existingLicenceCheck-${e.id}`}>
                    {e.existingLicenceCheck}
                </FriendlyBoolean>,
                hasMaximumChecks ? <Tooltip content={`Employee has reached maximum grey fleet checks for this account (${greyFleetSettings.vehiclesPerEmployee})`} position="left">{checkbox}</Tooltip> : checkbox
            ];
        });
        setTableRows(rows);
        setTotalRecords(employeesFromSearch.totalCount);
    }, [employeesFromSearch, selectedEmployee]);

    useEffect(() => {
        if (emailAndRegistration) {
            validateModelPage2();
        }
    }, [emailAndRegistration]);

    function fetchTableData(forceRefresh) {
        if (isLoadingOptions) {
            return;
        }

        if (
            currentPageNumber.current === pageNumber &&
            currentTableSortBy.current === sortedBy &&
            currentTableSortDir.current === sortedDir &&
            currentSearchTerm.current === debouncedSearchTerm &&
            currentAppliedFilters.current === appliedFilters &&
            currentLocations.current === selectedLocations.length &&
            !forceRefresh
        ) {
            //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
            return;
        }

        currentPageNumber.current = pageNumber;
        currentTableSortBy.current = sortedBy;
        currentTableSortDir.current = sortedDir;
        currentSearchTerm.current = debouncedSearchTerm;
        currentAppliedFilters.current = appliedFilters;
        currentLocations.current = selectedLocations.length;

        setIsLoading(true);

        const mappedFilters = mapFilters();

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            filters: mappedFilters,
            searchTerm: debouncedSearchTerm,
        };

        LicenceCheckingService.getGreyFleetEmployeeSearch(searchRequest)
            .then(
                (response) => {
                    setEmployeesFromSearch(response);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load employees. Please refresh the page."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not load employees. Please refresh the page."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map(function (
            f
        ) {
            return {
                key: f.name,
                value: f.displayName
            };
        });

        if (selectedLocations != null) {
            const mappedLocations = selectedLocations.map(function (l) {
                return { key: "LocationId", value: l };
            });

            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        return mappedFilters;
    }
    function onWizardChange(step) {
        setWizardStep(step);
    }

    function onWizardFinish() {
        setIsSaving(true);
        LicenceCheckingService.createGreyFleetCheck(
            selectedEmployee.id,
            emailAndRegistration
        )
            .then(
                () => {
                    navigate(
                        "/licence-checking/grey-fleet/checks?filter=NONE"
                    );

                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Grey Fleet Check created successfully."
                    );
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        NotificationToaster.show(
                            Intent.WARNING,
                            error.response.data
                        );
                    } else {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "Could not create Grey Fleet Check. Please refresh the page."
                        );
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not create Grey Fleet Check. Please refresh the page."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function validateModelPage2() {
        let rules = [
            { fieldName: "emailAddress", required: true, type: "email" },
            { fieldName: "registration", required: true, type: "alphanumeric" },
        ];

        let externalRules = [
            {
                fieldName: "requiredCoverTypeIds",
                valid: emailAndRegistration.requiredCoverTypeIds.length > 0,
                errorMessage: "At least one type of cover must be selected.",
            },
        ];

        validatePage2(rules, emailAndRegistration, externalRules);
    }

    function handleEmailChange(e) {
        setEmailAndRegistration((prev) => ({
            ...prev,
            emailAddress: e.target.value,
        }));
    }

    function handleRegistrationChange(e) {
        setEmailAndRegistration((prev) => ({
            ...prev,
            registration: e.target.value,
        }));
    }

    function handleIgnorePolicyCheckChange(e) {
        setEmailAndRegistration((prev) => ({
            ...prev,
            ignorePolicyCheck: e.target.checked,
        }));
    }

    // The menu ticks the options already chosen, so selecting one of those again clears it.
    function handleCoverTypeSelect(item) {
        setEmailAndRegistration((prev) => {
            let requiredCoverTypeIds;

            if (prev.requiredCoverTypeIds.includes(item.id)) {
                requiredCoverTypeIds = prev.requiredCoverTypeIds.filter((id) => id !== item.id);
            } else {
                requiredCoverTypeIds = [...prev.requiredCoverTypeIds, item.id];
            }

            return {
                ...prev,
                requiredCoverTypeIds,
            };
        });
    }

    // The tag input reports a removal using the rendered tag value, which is the cover type's name,
    // so it has to be resolved back to an id before it can be dropped from the selection.
    function handleCoverTypeRemove(name) {
        const removedCoverType = coverTypes.find((coverType) => coverType.name === name);

        if (removedCoverType == null) {
            return;
        }

        setEmailAndRegistration((prev) => ({
            ...prev,
            requiredCoverTypeIds: prev.requiredCoverTypeIds.filter((id) => id !== removedCoverType.id),
        }));
    }

    const selectedCoverTypeNames = coverTypes
        .filter((coverType) => emailAndRegistration.requiredCoverTypeIds.includes(coverType.id))
        .map((coverType) => coverType.name)
        .join(", ");

    let ignorePolicyCheckDisplay = "No";
    if (emailAndRegistration.ignorePolicyCheck) {
        ignorePolicyCheckDisplay = "Yes";
    }

    return (
        <div className="row">
            <Breadcrumbs items={breadcrumbs} />
            <h1>New Grey Fleet Check</h1>
            <Wizard
                onStepChange={onWizardChange}
                onFinish={onWizardFinish}
                canProceed={canProceed}
                disabled={isSaving}
                finishButtonText={"Start Check"}
                alignButtonsRight
                allowDirectStepNavigation={canProceed}
                additionalButtonsStart={
                    <LinkButton
                        intent="none"
                        text="Cancel"
                        href="/licence-checking/grey-fleet"
                    />
                }
            >
                <div>
                    <div className="spacer-bottom">
                        <h2>Select Employee</h2>
                        <p>
                            Please select from the employee list below. If an
                            employee does not have an email on their profile you
                            will be required to add one.
                        </p>
                    </div>
                    <div className="filter-controls">
                        <div className="inline-items spacer-bottom">
                            <div className="inline-item fill">
                                <div className="form-field-full">
                                    <FormTextInput
                                        placeholder="Search Employees"
                                        onChange={onSearchChange}
                                        value={searchTerm}
                                        large
                                        icon="search"
                                        id="onboarding-search-field"
                                        loading={isLoading || isLoadingOptions}
                                    />
                                </div>
                            </div>
                            <div className="inline-item">
                                <FormLocationSelector
                                    businessArea={"Tacho"}
                                    loading={isLoading || isLoadingOptions}
                                    selectedLocations={selectedLocations}
                                    setSelectedLocations={onSelectedLocationsChange}
                                    onLocationListClose={fetchTableData}
                                    useLocationDefaults={true}
                                    useHierarchy={useHierarchy}
                                    setUseHierarchy={setUseHierarchy}
                                    locationDefaultsToTrue={isLoading ? null : true}
                                />
                            </div>
                            <div className="inline-item">
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />                                            
                            </div>                            
                        </div>
                        <Filter
                            filterName={"licenceCheckingOnboarding"}
                            visible={showFilters}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            loading={isLoading || isLoadingOptions}
                            onClickOutside={onToggleFilter}
                        />
                    </div>
                    
                    <ListingTable
                        id="new-grey-fleet-check-employee-table"
                        pageSize={25}
                        headers={headers}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        pageable={true}
                        onPagingChange={onPagingChange}
                        loadingData={isLoading || isLoadingOptions}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        showPerPage={false}
                        onSort={onTableSort}
                    />
                </div>
                <div>
                    <h2>Employee and Vehicle Details</h2>
                    <div>
                        <FormTextInput
                            headingText="Selected employee email"
                            placeholder="Please enter the employee's email here"
                            onChange={handleEmailChange}
                            value={emailAndRegistration.emailAddress}
                            large
                            disabled={isSaving}
                            loading={isLoading}
                            wide
                            dangerHelperText={errorsPage2.emailAddress}
                        />
                        <NotificationInline
                            intent="info"
                            show={
                                wizardStep === 1 &&
                                originalEmail &&
                                originalEmail.trim() !== "" &&
                                emailAndRegistration.emailAddress.trim() !==
                                    "" &&
                                emailAndRegistration.emailAddress.trim() !==
                                    originalEmail.trim()
                            }
                            allowClose={true}
                            text="Updating the email address here will also update it on the employee's profile."
                        />
                    </div>
                    <div>
                        <FormTextInput
                            headingText="Vehicle registration"
                            placeholder="Please enter the vehicle registration here"
                            onChange={handleRegistrationChange}
                            value={emailAndRegistration.registration}
                            large
                            disabled={isSaving}
                            loading={isLoading}
                            wide
                            dangerHelperText={errorsPage2.registration}
                            maxLength={7}
                        />
                    </div>
                    <div>
                        <FormCheckbox
                            id="ignore-policy-check"
                            label="This vehicle does not have to comply with the company policy"
                            checked={emailAndRegistration.ignorePolicyCheck}
                            onChange={handleIgnorePolicyCheckChange}
                            disabled={isSaving}
                            loading={isLoading}
                        />
                    </div>
                    <div className="spacer-top">
                        <FormMultiSelect
                            id="required-cover-types"
                            headingText="Required type(s) of cover"
                            items={coverTypes}
                            loading={isLoading || isLoadingOptions}
                            disabled={isSaving}
                            placeholder="Please select"
                            wide
                            selectedValues={emailAndRegistration.requiredCoverTypeIds.map((id) => ({ id }))}
                            onItemSelect={handleCoverTypeSelect}
                            onRemove={handleCoverTypeRemove}
                            dangerHelperText={errorsPage2.requiredCoverTypeIds}
                        />
                    </div>
                </div>
                <div>
                    <h2>GreyFleet Check Details</h2>
                    <GridLayout numberOfColumns={3} gap={0}>
                        <FormReadonly
                            headingText="Employee Name"
                            value={selectedEmployee?.name}
                            bold
                        />
                        <FormReadonly
                            headingText="Employee Email"
                            value={emailAndRegistration.emailAddress}
                            bold
                        />
                        <FormReadonly
                            headingText="Vehicle Registration"
                            value={emailAndRegistration.registration}
                            bold
                        />
                        <FormReadonly
                            headingText="Required type(s) of cover"
                            value={selectedCoverTypeNames}
                            bold
                        />
                        <FormReadonly
                            headingText="Ignore Policy Check"
                            value={ignorePolicyCheckDisplay}
                            bold
                        />
                        <FormReadonly
                            headingText="Grey Fleet Credits"
                            value={"1"}
                            bold
                        />
                        <FormReadonly
                            headingText="Licence Checking Credits"
                            value={
                                selectedEmployee?.existingLicenceCheck
                                    ? "0"
                                    : "1"
                            }
                            bold
                        />
                    </GridLayout>
                </div>
            </Wizard>
            <AlertUnsavedData
                isDirty={(originalEmail !== emailAndRegistration.emailAddress || emailAndRegistration.registration !== "" || emailAndRegistration.ignorePolicyCheck || emailAndRegistration.requiredCoverTypeIds.length > 0) && !isSaving}
            />
        </div>
    );
}

GreyFleetCreate.propTypes = {
    breadcrumbs: PropTypes.array,
};
